import React, { useState, useMemo } from "react";
import { Eye, FileText } from "lucide-react";
import { PageHead } from "../components/PageHead";
import { DataTable } from "../components/DataTable";
import { Badge } from "../components/Badges";
import { RfqDetailModal } from "../components/RfqDetailModal";
import { fmtDate, money, fmtNum } from "../lib/format";
import { useAuth } from "../context/AuthContext";

const ST = {
  submitted: ["Submitted", "b-amber"],
  reviewed: ["Reviewed", "b-blue"],
  quoted: ["Quoted", "b-violet"],
  converted: ["Converted", "b-green"],
  cancelled: ["Cancelled", "b-slate"],
  draft: ["Draft", "b-slate"],
};

const TABS = [
  { key: "open", label: "Open", match: (r) => ["submitted", "reviewed", "quoted"].includes(r.status) },
  { key: "submitted", label: "New", match: (r) => r.status === "submitted" },
  { key: "reviewed", label: "Reviewed", match: (r) => r.status === "reviewed" },
  { key: "quoted", label: "Quoted", match: (r) => r.status === "quoted" },
  { key: "converted", label: "Converted", match: (r) => r.status === "converted" },
  { key: "cancelled", label: "Cancelled", match: (r) => r.status === "cancelled" },
  { key: "all", label: "All", match: () => true },
];

export default function RfqInbox() {
  const { can } = useAuth();
  const canEdit = can("rfqs", "edit");
  const [refresh, setRefresh] = useState(0);
  const [openId, setOpenId] = useState(null);
  const [tab, setTab] = useState("open");
  const [allRows, setAllRows] = useState([]);

  const def = TABS.find((t) => t.key === tab) || TABS[0];
  const filterRows = useMemo(() => (rows) => rows.filter(def.match), [def]);
  const counts = useMemo(() => {
    const c = {};
    TABS.forEach((t) => { c[t.key] = allRows.filter(t.match).length; });
    return c;
  }, [allRows]);

  const columns = [
    {
      key: "reference", label: "Reference", sortable: true, mono: true,
      render: (v, row) => (
        <button onClick={() => setOpenId(row.id)} data-testid={`rfq-open-${row.id}`}
          style={{ background: "none", border: 0, color: "var(--gold-deep)", cursor: "pointer", padding: 0, font: "inherit", fontWeight: 700, textDecoration: "underline" }}>
          {v}
        </button>
      ),
    },
    { key: "customer_name", label: "Customer", sortable: true, render: (v) => <span className="t-strong">{v || "—"}</span> },
    { key: "submitted_by_name", label: "Submitted by", sortable: true, render: (v) => <span className="t-mut">{v || "—"}</span> },
    { key: "item_count", label: "Items", sortable: true, render: (v) => <span className="t-mut">{v} item{v !== 1 ? "s" : ""}</span> },
    { key: "estimated_total", label: "Estimated", sortable: true, mono: true, render: (v) => money(v) },
    { key: "submitted_at", label: "Submitted", sortable: true, mono: true, render: (v) => <span className="t-mut">{fmtDate(v)}</span> },
    {
      key: "status", label: "Status", sortable: true, render: (v) => {
        const [label, cls] = ST[v] || [v, "b-slate"];
        return <Badge label={label} cls={cls} />;
      },
    },
    {
      key: "id", label: "", render: (_, row) => (
        <button className="btn sm ghost" onClick={() => setOpenId(row.id)} data-testid={`rfq-view-${row.id}`}>
          <Eye size={13} /> View
        </button>
      ),
    },
  ];

  const tabs = TABS.map((t) => (
    <button key={t.key} className={`ftab ${tab === t.key ? "active" : ""}`}
      onClick={() => setTab(t.key)} data-testid={`rfq-tab-${t.key}`}>
      {t.label}
      <span className="c">{fmtNum(counts[t.key] ?? 0)}</span>
    </button>
  ));

  return (
    <>
      <PageHead crumb="Front Desk" title="RFQ Inbox"
        sub="Customer-submitted quote requests — click any reference to see equipment, the submitter, and move it through the workflow." />

      <DataTable endpoint="/rfqs" columns={columns} rowKey="id" testid="rfq" clientSide
        defaultSort={{ by: "submitted_at", dir: "desc" }}
        searchPlaceholder="Search reference, customer, submitter…"
        refreshSignal={refresh}
        filterRows={filterRows}
        onLoaded={(rows) => setAllRows(rows)}
        toolbar={tabs}
        emptyText="No RFQs in this view." />

      {openId && (
        <RfqDetailModal id={openId} canEdit={canEdit}
          onClose={() => setOpenId(null)}
          onChanged={() => setRefresh((x) => x + 1)} />
      )}
    </>
  );
}
